import React, { useState, useEffect } from 'react';
import { X, Zap, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../stores/userStore';
import { TRANSITIONS } from '../../utils/animations';
import { usePromotions } from '../../hooks/usePromotions';

interface SpecialOfferBannerProps {
  onClose: () => void;
}

export default function SpecialOfferBanner({ onClose }: SpecialOfferBannerProps) {
  const [isExiting, setIsExiting] = useState(false);
  const navigate = useNavigate();
  const isPro = useUserStore(state => state.isPro);
  const isPremium = useUserStore(state => state.isPremium);
  const isAdmin = useUserStore(state => state.isAdmin);
  const { formattedTimeRemaining } = usePromotions();
  
  // Check if user has access to Pro (either Pro user or Admin)
  const hasProAccess = isPro() || isAdmin();
  const hasPremiumAccess = isPremium() || hasProAccess;

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleUpgrade = () => {
    navigate('/dashboard/precos');
    onClose();
  };

  // Don't show to Admin users
  if (isAdmin()) return null;

  // Determine which plan to promote based on user's current subscription
  const promotedPlan = hasProAccess ? null : (hasPremiumAccess ? 'pro' : 'premium');
  
  // If user already has Pro, don't show any promotion
  if (!promotedPlan) return null;

  return (
    <div className={`fixed bottom-6 left-1/2 transform -translate-x-1/2 max-w-md w-full mx-4 z-40 ${TRANSITIONS.DEFAULT} ${isExiting ? 'opacity-0 translate-y-10' : 'opacity-100'}`}>
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-xl overflow-hidden">
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 text-white/70 hover:text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="p-4 text-white">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <Zap className="h-5 w-5 text-yellow-300 animate-pulse" />
            </div>
            <div>
              <div className="text-xs text-yellow-300 font-semibold uppercase tracking-wider">Oferta Especial</div>
              <h3 className="text-lg font-bold">
                {promotedPlan === 'premium' 
                  ? '20% de desconto no plano Premium anual' 
                  : '20% de desconto no plano Pro anual'}
              </h3>
            </div>
          </div>
          
          <div className="flex items-center justify-between mb-3">
            <div className="text-white/90 text-sm">
              {promotedPlan === 'premium' 
                ? 'Economize R$120 no plano Premium anual'
                : 'Economize R$240 no plano Pro anual'}
            </div>
            <div className="flex items-center text-white/90 text-sm">
              <Clock className="h-4 w-4 mr-1" />
              <span>{formattedTimeRemaining}</span>
            </div>
          </div>
          
          <button
            onClick={handleUpgrade}
            className="w-full py-2 px-4 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-medium"
          >
            Aproveitar Oferta
          </button>
        </div>
      </div>
    </div>
  );
}